import { lighten } from "@mui/system";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import agent from "../api/agent";
import { PieChartProps } from "../component/features/pieChart/PieChart";
import { EquipmentColors } from "../config/enum";

export type IHomeTableFilterSession = 'tagNumber' | 'systemName' | 'equipmentType' | 'probabilityOfSuccess' | 'availability' | 'maintainability';
export interface IHomeTableEquipmentTable {
    availability: number;
    equipmentType: string;
    maintainability: number;
    probabilityOfSuccess: number;
    systemName: string;
    tagNumber: string;
}

export interface IHomeTableEquipmentChartResponse {
    title: string;
    data: {
        dataSets: {data: number[]}[];
        labels: string[]
    }
}

interface Chart {
    maintainability?: PieChartProps,
    reliability?: PieChartProps,
    availability?: PieChartProps,
}

interface LoadingChart {
    maintainability: boolean,
    reliability: boolean,
    availability: boolean,
}

export enum HomeTableEquipmentType {
    availability = 1,
    maintainability = 2,
    reliability = 0,
}

export interface GetPirChartParams {
    stationId: number;
    type: number;
}

export class HomeTableEquipmentStore {
    equipmentRam: IHomeTableEquipmentTable[] = [];
    loadingEquipmentRam = false;
    loadingDownload = false;
    loadingChart: LoadingChart = {
        maintainability: false,
        availability: false,
        reliability: false
    };


    chart: Chart = {}

    constructor() {
        makeAutoObservable(this)

        reaction(() => this.chart, (chart) => console.log(chart))
    }

    // Fetch data
    getEquipmentRam = async (stationId: number) => {
        this.loadingEquipmentRam = true
        try {
            let res = await agent.Dashboard.getEquipmentRam(String(stationId));
            res = res.map(i => {
                return {
                    ...i,
                    availability: Math.round(i.availability),
                    maintainability: Math.round(i.maintainability),
                    probabilityOfSuccess: Math.round(i.probabilityOfSuccess),
                }
            })
            runInAction(() => this.equipmentRam = res)
        } catch (error) {
            throw error;
        } finally {
            runInAction(() => this.loadingEquipmentRam = false);
        }
    }

    getChart = async (stationId: number, type: number) => {
        let currentType: keyof typeof this.loadingChart = 
            (type === HomeTableEquipmentType.availability && 'availability') ||
            (type === HomeTableEquipmentType.maintainability && 'maintainability') ||
            (type === HomeTableEquipmentType.reliability && 'reliability') ||
            'availability'

        this.loadingChart[currentType] = true
        
        try {
            const res = await agent.Dashboard.getPieChart({ stationId, type })

            const payload = this.handleChartData(res)

            runInAction(() => this.chart[currentType] = payload)
        } finally {
            runInAction(() => this.loadingChart[currentType] = false)
        }
    }
    
    private handleChartData = (data: IHomeTableEquipmentChartResponse): PieChartProps => {
        return {
            title: data.title,
            data: {
                ...data.data,
                datasets: [
                    {
                        backgroundColor: [
                            lighten(EquipmentColors.Red, .5), 
                            lighten(EquipmentColors.Orange, .5), 
                            lighten(EquipmentColors.Yellow, .5), 
                            lighten(EquipmentColors.Green, .5)
                        ],
                        borderColor: [
                            EquipmentColors.Red,
                            EquipmentColors.Orange,
                            EquipmentColors.Yellow,
                            EquipmentColors.Green
                        ],
                        borderWidth: 1,
                        data: data.data.dataSets[0].data,
                        label: 'Total'
                    }
                ]
            }
        }
    }

    downloadExcel = async (stationId: number) => {
        this.loadingDownload = true;
        try {
            await agent.Dashboard.downloadExcelEquipmentRam(stationId)
        } catch (error) {
            throw error
        } finally {
            runInAction(() => this.loadingDownload = false)
        }
    }
}
